import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type Ref } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Download,
  FileArchive,
  FileText,
  Link2,
  ListChecks,
  PenLine,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { Modale } from '../components/ui/Modale';
import { Onglets } from '../components/ui/Onglets';
import { attributsPanneau } from '../components/ui/onglets-aria';
import { Chargement, EnteteEcran, MessageErreur, Pastille } from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import {
  AvisValidation,
  ChampMotif,
  DemandeEnCours,
  motifValide,
} from '../components/modifications-sensibles/ElementsDemande';
import { ErreurApi } from '../lib/api';
import {
  lireNote,
  noteLisible,
  urlFichierDepotACorriger,
  useCorrections,
  useEnregistrerBrouillon,
  usePublierCorrection,
  useTravailACorriger,
  type DepotACorriger,
  type TravailACorriger,
  type TravailCorrige,
  type TravailDetail,
} from '../lib/useCorrections';
import { useDemanderModification } from '../lib/useModificationsSensibles';
import { momentRelatif } from '../lib/moment-relatif';
import { horodatageReunion } from '../lib/emargement-affichage';
import { echeanceLongue } from '../lib/affichage-devoirs';
import { tailleRessource } from '../lib/useLecteur';

/**
 * « Corrections » (écran `instructor-grading` de la maquette validée, TF-457).
 *
 * Tout vient de `/api/corrections` : les travaux rendus que le formateur
 * corrige — la direction les voit tous, avec leur correcteur —, puis ceux
 * dont la correction est publiée. Un travail se corrige d'une note sur 20 et
 * d'un commentaire, en brouillon puis publié ; l'apprenant ne lit rien avant.
 * Un quiz arrive avec sa note automatique, à valider ou ajuster. Une fois
 * publiée, la correction ne se modifie que par une demande à la direction
 * (TF-437). L'API dit ce qui empêche chaque geste : l'écran ne juge rien.
 *
 * `?travail=<devoir>:<apprenant>` ouvre un travail : c'est par là que le
 * tableau de bord, la notification de dépôt et la file de la direction y
 * mènent. L'adresse est vidée une fois lue.
 *
 * Écarts assumés avec la maquette : ni pré-correction ni reformulation par
 * l'IA (écartées le 25/10), ni critères de notation, ni export des
 * notes, ni vue Kanban, ni PDF ou note audio joints — rien de cela n'existe
 * en base.
 */

type OngletCorrections = 'a-corriger' | 'corriges';
type Priorite = '' | 'retard' | 'quiz' | 'brouillon';

interface Filtres {
  promotion: string;
  type: string;
  priorite: Priorite;
  correcteur: string;
}

const SANS_FILTRE: Filtres = { promotion: '', type: '', priorite: '', correcteur: '' };

const CLASSE_SELECT =
  'min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy [@media(pointer:coarse)]:min-h-[44px]';

function initiales(nom: string): string {
  return nom
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toUpperCase())
    .join('');
}

function pluriel(nombre: number, singulier: string, plurielForme = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielForme : singulier}`;
}

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

// ---------------------------------------------------------------------------
// File
// ---------------------------------------------------------------------------

function Avatar({ nom, grand = false }: { nom: string; grand?: boolean }): JSX.Element {
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full bg-codialis-navy font-bold text-white ${
        grand ? 'h-12 w-12 text-sm' : 'h-8 w-8 text-xs'
      }`}
      aria-hidden
    >
      {initiales(nom)}
    </div>
  );
}

function LigneFile({
  titre,
  apprenant,
  info,
  pastilles,
  active,
  onChoisir,
}: {
  titre: string;
  apprenant: string;
  info: string;
  pastilles: JSX.Element;
  active: boolean;
  onChoisir: () => void;
}): JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={onChoisir}
        aria-current={active ? 'true' : undefined}
        className={`flex w-full items-start gap-3 rounded-xl bg-white p-3 text-left shadow-card transition hover:shadow-md ${
          active ? 'ring-2 ring-codialis-mint' : ''
        }`}
      >
        <Avatar nom={apprenant} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-codialis-navy">{titre}</div>
          <div className="truncate text-xs text-codialis-gray">{apprenant}</div>
          <div className="mt-0.5 text-[10px] text-codialis-gray">{info}</div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">{pastilles}</div>
      </button>
    </li>
  );
}

function PastillesACorriger({ ligne }: { ligne: TravailACorriger }): JSX.Element {
  return (
    <>
      {ligne.enRetard ? (
        <Pastille texte="Rendu en retard" ton="danger" />
      ) : ligne.noteAutomatique ? (
        <Pastille texte="À valider" ton="mint" />
      ) : (
        <Pastille texte="À corriger" ton="warning" />
      )}
      {ligne.brouillon && <Pastille texte="Brouillon" ton="neutral" />}
    </>
  );
}

function infoACorriger(ligne: TravailACorriger, tousLesTravaux: boolean): string {
  const morceaux = [
    ligne.noteAutomatique
      ? `Note auto ${noteLisible(ligne.noteAutomatique.sur20)}/20`
      : ligne.depot !== null && ligne.depot > 1
        ? `Dépôt n°${ligne.depot}`
        : ligne.typeLibelle,
    `rendu ${momentRelatif(ligne.renduLe)}`,
  ];
  if (tousLesTravaux) morceaux.push(ligne.correcteur ?? 'sans correcteur actif');
  return morceaux.join(' · ');
}

function infoCorrige(ligne: TravailCorrige, tousLesTravaux: boolean): string {
  const morceaux = [`publiée ${momentRelatif(ligne.publieeLe)}`];
  if (tousLesTravaux && ligne.correcteur) morceaux.push(ligne.correcteur);
  return morceaux.join(' · ');
}

// ---------------------------------------------------------------------------
// Travail rendu
// ---------------------------------------------------------------------------

function LigneDepot({ depot, retenu, corrige }: { depot: DepotACorriger; retenu: boolean; corrige: boolean }): JSX.Element {
  const IconeFichier = depot.fichier?.typeMime === 'application/zip' ? FileArchive : FileText;
  return (
    <li className={`rounded-xl border p-3 ${retenu ? 'border-codialis-mint/40 bg-white' : 'border-gray-100 bg-gray-50'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-codialis-navy">Dépôt n°{depot.numero}</span>
        <span className="text-xs text-codialis-gray">{echeanceLongue(depot.deposeLe)}</span>
        {depot.enRetard && <Pastille texte="En retard" ton="warning" />}
        {retenu ? (
          <Pastille texte={corrige ? 'Corrigé' : 'À corriger'} ton="mint" />
        ) : (
          <Pastille texte="Remplacé" ton="neutral" />
        )}
      </div>
      <ul className="mt-2 space-y-1.5">
        {depot.fichier && (
          <li className="flex min-w-0 items-center gap-2 text-sm">
            <IconeFichier className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden />
            <a
              href={urlFichierDepotACorriger(depot.id)}
              className="min-w-0 truncate font-medium text-codialis-sky hover:underline"
              download
            >
              {depot.fichier.nom}
            </a>
            <span className="shrink-0 text-xs text-codialis-gray">{tailleRessource(depot.fichier.tailleOctets)}</span>
            <Download className="h-3.5 w-3.5 shrink-0 text-codialis-gray" aria-hidden />
          </li>
        )}
        {depot.liens.map((lien) => (
          <li key={lien} className="flex min-w-0 items-center gap-2 text-sm">
            <Link2 className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden />
            <a
              href={lien}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 truncate font-medium text-codialis-sky hover:underline"
            >
              {lien}
            </a>
          </li>
        ))}
      </ul>
    </li>
  );
}

function DetailQuiz({ quiz }: { quiz: NonNullable<TravailDetail['quiz']> }): JSX.Element {
  return (
    <ol className="space-y-3">
      {quiz.detail.map((question) => (
        <li key={question.id} className="rounded-xl border border-gray-100 bg-white p-3">
          <div className="flex items-start gap-2">
            {question.correcte ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-codialis-teal" aria-label="Bonne réponse" />
            ) : (
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-label="Réponse fausse" />
            )}
            <p className="min-w-0 text-sm font-semibold text-codialis-navy">
              Q{question.numero}. {question.enonce}
            </p>
          </div>
          {!question.jouee && <p className="mt-1 pl-6 text-xs text-codialis-gray">Pas de réponse : le temps s’est écoulé.</p>}
          <ul className="mt-2 space-y-1 pl-6">
            {question.options.map((option) => (
              <li
                key={option.lettre}
                className={`flex min-w-0 items-start gap-2 text-xs ${
                  option.correcte ? 'font-semibold text-codialis-teal' : option.choisie ? 'text-red-600' : 'text-codialis-gray'
                }`}
              >
                <span className="shrink-0">{option.lettre}.</span>
                <span className="min-w-0 break-words">{option.texte}</span>
                {option.choisie && <span className="shrink-0">· réponse donnée</span>}
              </li>
            ))}
          </ul>
          {question.explication && (
            <p className="mt-2 pl-6 text-xs italic text-codialis-gray">{question.explication}</p>
          )}
        </li>
      ))}
    </ol>
  );
}

function BlocNoteAutomatique({
  quiz,
  peutCorriger,
  onValider,
  onAjuster,
}: {
  quiz: NonNullable<TravailDetail['quiz']>;
  peutCorriger: boolean;
  onValider: () => void;
  onAjuster: () => void;
}): JSX.Element {
  const [detail, setDetail] = useState(false);
  const erreurs = quiz.questions - quiz.bonnes;
  return (
    <section className="rounded-2xl border-2 border-codialis-mint/30 bg-gradient-to-br from-codialis-mint/10 via-white to-codialis-sky/10 p-4 sm:p-5">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <ListChecks className="h-5 w-5 text-codialis-teal" aria-hidden />
        <h3 className="font-display font-bold text-codialis-navy">Note automatique du quiz</h3>
        <span className="text-xs text-codialis-gray sm:ml-auto">
          {quiz.tempsEcoule ? 'Temps écoulé' : quiz.termineeLe ? `Terminé le ${horodatageReunion(quiz.termineeLe)}` : ''}
        </span>
      </div>
      <div className="mb-4 grid grid-cols-3 gap-2 sm:gap-4">
        <div className="text-center">
          <div className="font-display text-3xl font-extrabold text-codialis-teal sm:text-4xl">
            {noteLisible(quiz.noteAutomatique)}
            <span className="text-base text-codialis-gray">/20</span>
          </div>
          <div className="text-xs text-codialis-gray">Note proposée</div>
        </div>
        <div className="text-center">
          <div className="font-display text-3xl font-extrabold text-codialis-navy sm:text-4xl">
            {quiz.bonnes}
            <span className="text-base text-codialis-gray">/{quiz.questions}</span>
          </div>
          <div className="text-xs text-codialis-gray">Bonnes réponses</div>
        </div>
        <div className="text-center">
          <div className="font-display text-3xl font-extrabold text-amber-500 sm:text-4xl">{erreurs}</div>
          <div className="text-xs text-codialis-gray">Erreur{erreurs > 1 ? 's' : ''} à reprendre</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {peutCorriger && (
          <>
            <button
              type="button"
              onClick={onValider}
              className="rounded-lg bg-codialis-mint px-4 py-2 text-sm font-bold text-codialis-navy transition hover:bg-codialis-teal [@media(pointer:coarse)]:min-h-[44px]"
            >
              Valider {noteLisible(quiz.noteAutomatique)}/20
            </button>
            <button
              type="button"
              onClick={onAjuster}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-codialis-navy hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[44px]"
            >
              Ajuster
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => setDetail(!detail)}
          aria-expanded={detail}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-codialis-navy hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[44px]"
        >
          {detail ? 'Masquer le détail' : 'Voir le détail par question'}
          <ChevronDown className={`h-4 w-4 transition ${detail ? 'rotate-180' : ''}`} aria-hidden />
        </button>
      </div>
      {detail && (
        <div className="mt-4">
          <DetailQuiz quiz={quiz} />
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Correction
// ---------------------------------------------------------------------------

function ChampNote({
  id,
  valeur,
  onChange,
  invalide,
  refChamp,
}: {
  id: string;
  valeur: string;
  onChange: (valeur: string) => void;
  invalide: boolean;
  refChamp?: Ref<HTMLInputElement>;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-xs font-semibold uppercase tracking-wide text-codialis-navy">
        Note
      </label>
      <div className="flex items-center gap-2">
        <input
          id={id}
          ref={refChamp}
          type="text"
          inputMode="decimal"
          value={valeur}
          onChange={(evenement) => onChange(evenement.target.value)}
          placeholder="14,5"
          aria-invalid={invalide || undefined}
          aria-describedby={`${id}-aide`}
          className={`w-24 rounded-lg border px-3 py-2 text-center text-lg font-bold text-codialis-navy focus:outline-none focus:ring-2 focus:ring-codialis-mint [@media(pointer:coarse)]:min-h-[44px] ${
            invalide ? 'border-red-300' : 'border-gray-200'
          }`}
        />
        <span className="text-sm text-codialis-gray">/ 20</span>
      </div>
      <p id={`${id}-aide`} className={`mt-1 text-[11px] ${invalide ? 'text-red-600' : 'text-codialis-gray'}`}>
        De 0 à 20, au dixième près (ex. 14,5).
      </p>
    </div>
  );
}

function ChampCommentaire({
  id,
  valeur,
  onChange,
  max,
}: {
  id: string;
  valeur: string;
  onChange: (valeur: string) => void;
  max: number;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-xs font-semibold uppercase tracking-wide text-codialis-navy">
        Commentaire pour l’apprenant
      </label>
      <textarea
        id={id}
        rows={4}
        value={valeur}
        maxLength={max}
        onChange={(evenement) => onChange(evenement.target.value)}
        placeholder="Votre retour personnalisé…"
        className="w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
      />
      <p className="mt-1 text-right text-[11px] text-codialis-gray">
        {valeur.length} / {max}
      </p>
    </div>
  );
}

function FormulaireCorrection({ travail }: { travail: TravailDetail }): JSX.Element {
  const toast = useToast();
  const idNote = useId();
  const idCommentaire = useId();
  const champNote = useRef<HTMLInputElement>(null);
  const brouillon = useEnregistrerBrouillon();
  const publier = usePublierCorrection();
  const noteEnregistree = travail.correction?.note ?? null;
  const [note, setNote] = useState(noteEnregistree === null ? '' : noteLisible(noteEnregistree));
  const [commentaire, setCommentaire] = useState(travail.correction?.commentaire ?? '');
  const [confirmation, setConfirmation] = useState(false);
  const [noteTouchee, setNoteTouchee] = useState(false);
  const lue = lireNote(note);
  const noteInvalide = noteTouchee && note.trim() !== '' && lue === null;
  const dernierDepot = travail.depots[0] ?? null;
  const erreur = brouillon.error ?? publier.error;

  const saisirNote = (valeur: string) => {
    setNoteTouchee(true);
    setNote(valeur);
  };

  const enregistrer = () => {
    if (note.trim() !== '' && lue === null) {
      setNoteTouchee(true);
      return;
    }
    brouillon.mutate(
      { cle: travail.cle, note: lue, commentaire: commentaire.trim() || null },
      { onSuccess: (resultat) => toast(resultat.message) },
    );
  };

  const demanderPublication = (evenement?: FormEvent) => {
    evenement?.preventDefault();
    setNoteTouchee(true);
    if (lue === null) {
      champNote.current?.focus();
      return;
    }
    setConfirmation(true);
  };

  const confirmer = () => {
    if (lue === null) return;
    publier.mutate(
      { cle: travail.cle, note: lue, commentaire: commentaire.trim() || null, depotId: dernierDepot?.id ?? null },
      {
        onSuccess: (resultat) => {
          setConfirmation(false);
          toast(resultat.message);
        },
        onError: () => setConfirmation(false),
      },
    );
  };

  return (
    <form onSubmit={demanderPublication} className="space-y-5">
      {travail.quiz && (
        <BlocNoteAutomatique
          quiz={travail.quiz}
          peutCorriger
          onValider={() => {
            setNote(noteLisible(travail.quiz!.noteAutomatique));
            setNoteTouchee(true);
            setConfirmation(true);
          }}
          onAjuster={() => champNote.current?.focus()}
        />
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[auto_1fr]">
        <ChampNote id={idNote} valeur={note} onChange={saisirNote} invalide={noteInvalide} refChamp={champNote} />
        <ChampCommentaire
          id={idCommentaire}
          valeur={commentaire}
          onChange={setCommentaire}
          max={travail.limites.commentaireMax}
        />
      </div>

      {travail.correction && !travail.correction.publiee && (
        <p className="text-xs text-codialis-gray">
          Brouillon enregistré le {horodatageReunion(travail.correction.modifieeLe)}
          {travail.correction.modifieePar ? ` par ${travail.correction.modifieePar}` : ''} : l’apprenant ne le voit pas.
        </p>
      )}
      {erreur && <MessageErreur message={messageErreur(erreur, 'La correction n’a pas pu être enregistrée.')} />}

      <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={enregistrer}
          disabled={brouillon.isPending}
          className="rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-codialis-navy hover:bg-gray-50 disabled:opacity-60 [@media(pointer:coarse)]:min-h-[44px]"
        >
          {brouillon.isPending ? 'Enregistrement…' : 'Enregistrer brouillon'}
        </button>
        <button
          type="submit"
          disabled={publier.isPending}
          className="rounded-lg bg-codialis-navy px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-60 [@media(pointer:coarse)]:min-h-[44px]"
        >
          Publier la correction
        </button>
      </div>

      <Modale
        titre="Publier la correction"
        sousTitre={`${travail.devoir.titre} · ${travail.apprenant.nom}`}
        ouverte={confirmation}
        onFermer={() => setConfirmation(false)}
        largeur="max-w-lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-codialis-navy">
            Note publiée : <strong>{lue === null ? '—' : `${noteLisible(lue)}/20`}</strong>
            {commentaire.trim() ? ', avec votre commentaire.' : ', sans commentaire.'}
          </p>
          <p className="flex items-start gap-2 rounded-xl bg-codialis-sand px-3 py-2 text-xs text-codialis-navy">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
            <span>
              L’apprenant est prévenu et lit aussitôt sa note
              {dernierDepot ? `, et ne peut plus déposer de nouvelle version (dépôt corrigé : n°${dernierDepot.numero})` : ''}.
              Ensuite, une modification passe par une demande à la direction.
            </span>
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setConfirmation(false)}
              className="rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-codialis-navy hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[44px]"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={confirmer}
              disabled={publier.isPending || lue === null}
              className="rounded-lg bg-codialis-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-codialis-deep disabled:opacity-60 [@media(pointer:coarse)]:min-h-[44px]"
            >
              {publier.isPending ? 'Publication…' : 'Publier'}
            </button>
          </div>
        </div>
      </Modale>
    </form>
  );
}

function DemandeModification({ travail, onFermer }: { travail: TravailDetail; onFermer: () => void }): JSX.Element {
  const correction = travail.correction!;
  const toast = useToast();
  const demander = useDemanderModification();
  const idNote = useId();
  const idCommentaire = useId();
  const [note, setNote] = useState(correction.note === null ? '' : noteLisible(correction.note));
  const [commentaire, setCommentaire] = useState(correction.commentaire ?? '');
  const [motif, setMotif] = useState('');
  const lue = lireNote(note);
  const directe = travail.validationDirecte;

  const envoyer = (evenement: FormEvent) => {
    evenement.preventDefault();
    if (lue === null || !motifValide(motif)) return;
    demander.mutate(
      { type: 'NOTE_DEVOIR', correctionId: correction.id, note: lue, commentaire: commentaire.trim() || null, motif: motif.trim() },
      {
        onSuccess: (resultat) => {
          toast(resultat.message);
          onFermer();
        },
      },
    );
  };

  return (
    <form onSubmit={envoyer} className="space-y-4 rounded-2xl border border-gray-100 bg-gray-50 p-4">
      <AvisValidation directe={directe} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[auto_1fr]">
        <ChampNote id={idNote} valeur={note} onChange={setNote} invalide={note.trim() !== '' && lue === null} />
        <ChampCommentaire id={idCommentaire} valeur={commentaire} onChange={setCommentaire} max={travail.limites.commentaireMax} />
      </div>
      <ChampMotif
        valeur={motif}
        onChange={setMotif}
        directe={directe}
        placeholder="Ce qui justifie la modification (erreur de barème, question annulée…)"
      />
      {demander.error && <MessageErreur message={messageErreur(demander.error, 'La demande n’a pas pu être envoyée.')} />}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onFermer}
          className="rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-codialis-navy hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[44px]"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={demander.isPending || lue === null || !motifValide(motif)}
          className="rounded-lg bg-codialis-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-codialis-deep disabled:opacity-60 [@media(pointer:coarse)]:min-h-[44px]"
        >
          {demander.isPending ? 'Envoi…' : directe ? 'Appliquer la modification' : 'Envoyer la demande'}
        </button>
      </div>
    </form>
  );
}

function CorrectionPubliee({ travail }: { travail: TravailDetail }): JSX.Element {
  const correction = travail.correction!;
  const [modifier, setModifier] = useState(false);
  return (
    <div className="space-y-4">
      {travail.quiz && <BlocNoteAutomatique quiz={travail.quiz} peutCorriger={false} onValider={() => {}} onAjuster={() => {}} />}
      <section className="rounded-2xl border border-codialis-mint/30 bg-white p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-codialis-teal" aria-hidden />
          <h3 className="font-display font-bold text-codialis-navy">Correction publiée</h3>
          {correction.rectifieeLe && <Pastille texte="Rectifiée" ton="info" />}
        </div>
        <p className="mt-1 text-xs text-codialis-gray">
          {correction.publieeLe ? `Le ${horodatageReunion(correction.publieeLe)}` : ''}
          {correction.publieePar ? ` par ${correction.publieePar}` : ''}
          {correction.depot !== null ? ` · dépôt n°${correction.depot}` : ''}
          {correction.rectifieeLe ? ` · rectifiée le ${horodatageReunion(correction.rectifieeLe)}` : ''}
        </p>
        <div className="font-display mt-3 text-4xl font-extrabold text-codialis-navy">
          {correction.note === null ? '—' : noteLisible(correction.note)}
          <span className="text-base text-codialis-gray">/20</span>
        </div>
        <p className="mt-3 whitespace-pre-line break-words text-sm text-codialis-navy">
          {correction.commentaire ?? <span className="text-codialis-gray">Sans commentaire.</span>}
        </p>
      </section>

      {travail.demandeEnAttente ? (
        <DemandeEnCours demande={travail.demandeEnAttente} />
      ) : modifier ? (
        <DemandeModification travail={travail} onFermer={() => setModifier(false)} />
      ) : travail.refusDemande ? (
        <p className="text-xs text-codialis-gray">{travail.refusDemande}</p>
      ) : (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setModifier(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-codialis-navy hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[44px]"
          >
            <PenLine className="h-4 w-4" aria-hidden />
            Demander une modification
          </button>
        </div>
      )}
    </div>
  );
}

function PanneauCorrection({ cle }: { cle: string }): JSX.Element {
  const { data: travail, isLoading, error } = useTravailACorriger(cle);
  if (isLoading) return <Chargement libelle="Chargement du travail…" />;
  if (error || !travail) return <MessageErreur message={messageErreur(error, 'Ce travail n’a pas pu être chargé.')} />;

  const publiee = travail.correction?.publiee ?? false;
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="border-b border-gray-100 bg-gradient-to-r from-codialis-mint/10 to-codialis-sky/10 p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <Avatar nom={travail.apprenant.nom} grand />
            <div className="min-w-0">
              <div className="font-display truncate text-base font-bold text-codialis-navy">{travail.apprenant.nom}</div>
              <div className="text-xs text-codialis-gray">
                {travail.apprenant.promotion} · {travail.apprenant.siteCode}
                {travail.renduLe ? ` · rendu ${momentRelatif(travail.renduLe)}` : ''}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {publiee ? (
              <Pastille texte="Corrigé" ton="success" />
            ) : travail.correction ? (
              <Pastille texte="Brouillon" ton="neutral" />
            ) : null}
            {travail.enRetard && <Pastille texte="Rendu en retard" ton="danger" />}
            <Pastille texte={travail.devoir.typeLibelle} ton="info" />
          </div>
        </div>
      </div>

      <div className="space-y-5 p-4 sm:p-5">
        <section>
          <h3 className="font-display font-bold text-codialis-navy">{travail.devoir.titre}</h3>
          <p className="text-xs text-codialis-gray">
            {travail.devoir.contexte} · échéance {echeanceLongue(travail.devoir.echeance)}
            {travail.correcteur ? ` · correcteur : ${travail.correcteur}` : ''}
          </p>
          {travail.devoir.consigne && (
            <details className="mt-2 text-sm text-codialis-gray">
              <summary className="cursor-pointer font-medium text-codialis-sky [@media(pointer:coarse)]:min-h-[40px]">
                Consignes
              </summary>
              <p className="mt-1 whitespace-pre-line">{travail.devoir.consigne}</p>
            </details>
          )}
        </section>

        {travail.devoir.type !== 'QUIZ' && (
          <section>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-codialis-navy">
              {travail.depots.length > 1 ? 'Dépôts de l’apprenant' : 'Dépôt de l’apprenant'}
            </h4>
            <ul className="space-y-2">
              {travail.depots.map((depot, rang) => (
                <LigneDepot
                  key={depot.id}
                  depot={depot}
                  retenu={publiee ? depot.numero === travail.correction?.depot : rang === 0}
                  corrige={publiee}
                />
              ))}
            </ul>
          </section>
        )}

        {publiee ? (
          <CorrectionPubliee travail={travail} />
        ) : travail.refusBrouillon ? (
          <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-codialis-gray">{travail.refusBrouillon}</p>
        ) : (
          // Le formulaire repart des valeurs enregistrées à chaque travail ouvert.
          <FormulaireCorrection key={travail.cle} travail={travail} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Écran
// ---------------------------------------------------------------------------

/** Sous la file, sur téléphone et tablette : le panneau du travail choisi vient à l'écran. */
function montrerPanneau(element: HTMLElement | null): void {
  if (!window.matchMedia?.('(max-width: 1023px)').matches) return;
  requestAnimationFrame(() => element?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }));
}

function filtrer<T extends { promotion: string; type: string; correcteur: string | null }>(
  lignes: readonly T[],
  filtres: Filtres,
  priorite: (ligne: T) => boolean,
): T[] {
  return lignes.filter(
    (ligne) =>
      (!filtres.promotion || ligne.promotion === filtres.promotion) &&
      (!filtres.type || (filtres.type === 'QUIZ' ? ligne.type === 'QUIZ' : ligne.type !== 'QUIZ')) &&
      (!filtres.correcteur || (ligne.correcteur ?? '') === filtres.correcteur) &&
      priorite(ligne),
  );
}

export function InstructorGradingPage(): JSX.Element {
  const { data, isLoading, error } = useCorrections();
  const [parametres, setParametres] = useSearchParams();
  const [onglet, setOnglet] = useState<OngletCorrections>('a-corriger');
  const [filtres, setFiltres] = useState<Filtres>(SANS_FILTRE);
  const [selection, setSelection] = useState<string | null>(null);
  const panneau = useRef<HTMLDivElement>(null);

  // `?travail=` ouvre un travail, sous l'onglet qui le montre ; l'adresse est vidée une fois lue.
  const demande = parametres.get('travail');
  useEffect(() => {
    if (!demande || !data) return;
    setSelection(demande);
    montrerPanneau(panneau.current);
    setOnglet(data.corriges.some((ligne) => ligne.cle === demande) ? 'corriges' : 'a-corriger');
    setFiltres(SANS_FILTRE);
    const suivants = new URLSearchParams(parametres);
    suivants.delete('travail');
    setParametres(suivants, { replace: true });
  }, [demande, data, parametres, setParametres]);

  const aCorriger = useMemo(
    () =>
      data
        ? filtrer(data.aCorriger, filtres, (ligne) =>
            filtres.priorite === 'retard'
              ? ligne.enRetard
              : filtres.priorite === 'quiz'
                ? ligne.noteAutomatique !== null
                : filtres.priorite === 'brouillon'
                  ? ligne.brouillon
                  : true,
          )
        : [],
    [data, filtres],
  );
  const corriges = useMemo(
    () => (data ? filtrer(data.corriges, filtres, () => true) : []),
    [data, filtres],
  );

  if (isLoading) return <Chargement libelle="Chargement des corrections…" />;
  if (error || !data) return <MessageErreur message={messageErreur(error, 'Les corrections n’ont pas pu être chargées.')} />;

  const lignes = onglet === 'a-corriger' ? aCorriger : corriges;
  // Un travail choisi reste ouvert — publié, il quitte la file sans quitter l'écran ;
  // demandé par l'adresse, il s'ouvre même hors des listes, l'API dit s'il est au lecteur.
  const ouverte = selection ?? lignes[0]?.cle ?? null;
  const toutes = onglet === 'a-corriger' ? data.aCorriger : data.corriges;
  const promotions = [...new Set(toutes.map((ligne) => ligne.promotion))].sort((a, b) => a.localeCompare(b, 'fr'));
  const correcteurs = [...new Set(toutes.map((ligne) => ligne.correcteur ?? ''))].sort((a, b) => a.localeCompare(b, 'fr'));
  const filtre = Object.values(filtres).some(Boolean);
  const { compteurs } = data;
  const enAttente = compteurs.devoirs + compteurs.quiz;

  const choisir = (cle: string) => {
    setSelection(cle);
    montrerPanneau(panneau.current);
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Corrections"
        sousTitre={
          enAttente === 0
            ? 'Aucun travail en attente de correction'
            : [
                pluriel(enAttente, 'travail en attente', 'travaux en attente'),
                compteurs.quiz > 0 ? `${pluriel(compteurs.quiz, 'quiz', 'quiz')} à valider` : null,
                compteurs.enRetard > 0 ? `${compteurs.enRetard} rendu${compteurs.enRetard > 1 ? 's' : ''} en retard` : null,
              ]
                .filter(Boolean)
                .join(' · ')
        }
      />

      <Onglets
        etiquette="Corrections"
        onglets={[
          { cle: 'a-corriger', libelle: 'À corriger', compte: enAttente },
          { cle: 'corriges', libelle: 'Corrigés', compte: compteurs.corriges },
        ]}
        actif={onglet}
        onChanger={(cle) => {
          setOnglet(cle);
          setSelection(null);
        }}
        idPanneau="panneau-corrections"
        actions={
          <>
            <select
              aria-label="Promotion"
              value={filtres.promotion}
              onChange={(evenement) => setFiltres({ ...filtres, promotion: evenement.target.value })}
              className={CLASSE_SELECT}
            >
              <option value="">Toutes promotions</option>
              {promotions.map((promotion) => (
                <option key={promotion} value={promotion}>
                  {promotion}
                </option>
              ))}
            </select>
            <select
              aria-label="Type"
              value={filtres.type}
              onChange={(evenement) => setFiltres({ ...filtres, type: evenement.target.value })}
              className={CLASSE_SELECT}
            >
              <option value="">Tous types</option>
              <option value="QUIZ">Quiz</option>
              <option value="DEVOIR">Devoirs à rendre</option>
            </select>
            {onglet === 'a-corriger' && (
              <select
                aria-label="Priorité"
                value={filtres.priorite}
                onChange={(evenement) => setFiltres({ ...filtres, priorite: evenement.target.value as Priorite })}
                className={CLASSE_SELECT}
              >
                <option value="">Toutes priorités</option>
                <option value="retard">Rendus en retard</option>
                <option value="quiz">Quiz à valider</option>
                <option value="brouillon">Brouillons</option>
              </select>
            )}
            {data.tousLesTravaux && (
              <select
                aria-label="Correcteur"
                value={filtres.correcteur}
                onChange={(evenement) => setFiltres({ ...filtres, correcteur: evenement.target.value })}
                className={CLASSE_SELECT}
              >
                <option value="">Tous correcteurs</option>
                {correcteurs.map((nom) => (
                  <option key={nom} value={nom}>
                    {nom || 'Sans correcteur actif'}
                  </option>
                ))}
              </select>
            )}
            {filtre && (
              <button
                type="button"
                onClick={() => setFiltres(SANS_FILTRE)}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                Réinitialiser
              </button>
            )}
          </>
        }
      />

      <div {...attributsPanneau('panneau-corrections', onglet)} className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="min-w-0 space-y-3 lg:col-span-1" aria-label="File de correction">
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-codialis-navy">
            {onglet === 'a-corriger' ? `File de correction (${aCorriger.length})` : `Corrections publiées (${corriges.length})`}
          </h2>
          {lignes.length === 0 ? (
            <p className="rounded-xl bg-white p-4 text-sm text-codialis-gray shadow-card">
              {filtre
                ? 'Aucun travail ne correspond à ces filtres.'
                : onglet === 'a-corriger'
                  ? 'Un devoir rendu ou un quiz terminé par l’un de vos apprenants s’ajoute ici jusqu’à la publication de sa correction.'
                  : 'Aucune correction publiée pour le moment.'}
            </p>
          ) : (
            <ul className="space-y-3">
              {onglet === 'a-corriger'
                ? aCorriger.map((ligne) => (
                    <LigneFile
                      key={ligne.cle}
                      titre={`${ligne.titre} · ${ligne.typeLibelle}`}
                      apprenant={`${ligne.apprenant} · ${ligne.promotion}`}
                      info={infoACorriger(ligne, data.tousLesTravaux)}
                      pastilles={<PastillesACorriger ligne={ligne} />}
                      active={ligne.cle === ouverte}
                      onChoisir={() => choisir(ligne.cle)}
                    />
                  ))
                : corriges.map((ligne) => (
                    <LigneFile
                      key={ligne.cle}
                      titre={`${ligne.titre} · ${ligne.typeLibelle}`}
                      apprenant={`${ligne.apprenant} · ${ligne.promotion}`}
                      info={infoCorrige(ligne, data.tousLesTravaux)}
                      pastilles={
                        <>
                          <Pastille texte={`${noteLisible(ligne.note)}/20`} ton="success" />
                          {ligne.demandeEnAttente && <Pastille texte="Modification demandée" ton="warning" />}
                          {ligne.rectifieeLe && !ligne.demandeEnAttente && <Pastille texte="Rectifiée" ton="info" />}
                        </>
                      }
                      active={ligne.cle === ouverte}
                      onChoisir={() => choisir(ligne.cle)}
                    />
                  ))}
            </ul>
          )}
        </section>

        <div ref={panneau} className="min-w-0 scroll-mt-4 lg:col-span-2">
          {ouverte ? (
            <PanneauCorrection cle={ouverte} />
          ) : (
            <div className="rounded-2xl bg-white p-6 text-center text-sm text-codialis-gray shadow-card">
              Choisissez un travail dans la file pour le corriger.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
